"use strict";
Object.defineProperty(exports, Symbol.toStringTag, { value: "Module" });
const store = require("@tanstack/store");
const utils = require("./utils.cjs");
const eventClient = require("./event-client.cjs");
function getDefaultAsyncRetryerState() {
  return structuredClone({
    currentAttempt: 0,
    executionCount: 0,
    isExecuting: false,
    lastError: void 0,
    lastExecutionTime: 0,
    lastResult: void 0,
    status: "idle",
    totalExecutionTime: 0
  });
}
function asyncRetryerOptions(options) {
  return options;
}
const defaultOptions = {
  backoff: "exponential",
  baseWait: 1e3,
  enabled: true,
  jitter: 0,
  maxAttempts: 3,
  maxExecutionTime: Infinity,
  maxTotalExecutionTime: Infinity,
  throwOnError: "last"
};
class AsyncRetryer {
  /**
   * Creates a new AsyncRetryer instance
   * @param fn The async function to retry
   * @param initialOptions Configuration options for the retryer
   */
  constructor(fn, initialOptions = {}) {
    this.fn = fn;
    this.store = new store.Store(
      getDefaultAsyncRetryerState()
    );
    this.#abortController = null;
    this._emit = () => eventClient.emitChange("AsyncRetryer", this);
    this.setOptions = (newOptions) => {
      this.options = { ...this.options, ...newOptions };
    };
    this.#setState = (newState) => {
      this.store.setState((state) => {
        const combinedState = {
          ...state,
          ...newState
        };
        const { isExecuting, currentAttempt } = combinedState;
        return {
          ...combinedState,
          status: !this.#getEnabled() ? "disabled" : isExecuting && currentAttempt === 1 ? "executing" : isExecuting && currentAttempt > 1 ? "retrying" : "idle"
        };
      });
      eventClient.emitChange("AsyncRetryer", this);
    };
    this.#getEnabled = () => {
      return !!utils.parseFunctionOrValue(this.options.enabled, this);
    };
    this.#getMaxAttempts = () => {
      return utils.parseFunctionOrValue(this.options.maxAttempts, this);
    };
    this.#getBaseWait = () => {
      return utils.parseFunctionOrValue(this.options.baseWait, this);
    };
    this.#calculateJitter = (waitTime) => {
      const jitterAmount = this.options.jitter;
      if (jitterAmount <= 0) return 0;
      try {
        const crypto = typeof globalThis !== "undefined" ? globalThis.crypto : void 0;
        if (crypto?.getRandomValues) {
          const array = new Uint32Array(1);
          crypto.getRandomValues(array);
          const randomFactor = array[0] / 4294967295 * 2 - 1;
          return Math.floor(waitTime * jitterAmount * randomFactor);
        }
      } catch {
      }
      return 0;
    };
    this.#calculateWait = (attempt) => {
      const baseWait = this.#getBaseWait();
      let waitTime;
      switch (this.options.backoff) {
        case "linear":
          waitTime = baseWait * attempt;
          break;
        case "exponential":
          waitTime = baseWait * Math.pow(2, attempt - 1);
          break;
        case "fixed":
        default:
          waitTime = baseWait;
          break;
      }
      const jitter = this.#calculateJitter(waitTime);
      return Math.max(0, waitTime + jitter);
    };
    this.execute = async (...args) => {
      if (!this.#getEnabled()) {
        return void 0;
      }
      this.abort("new-execution");
      const startTime = Date.now();
      let lastError;
      let result;
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;
      this.#setState({
        isExecuting: true,
        currentAttempt: 0,
        lastError: void 0
      });
      let totalTimeoutId;
      if (this.options.maxTotalExecutionTime !== Infinity) {
        totalTimeoutId = setTimeout(() => {
          this.options.onTotalExecutionTimeout?.(this);
          this.abort("total-timeout");
        }, this.options.maxTotalExecutionTime);
      }
      let isLastAttempt = false;
      for (let attempt = 1; attempt <= this.#getMaxAttempts(); attempt++) {
        isLastAttempt = attempt === this.#getMaxAttempts();
        this.#setState({ currentAttempt: attempt });
        try {
          if (signal.aborted) {
            return void 0;
          }
          const currentTotalTime = Date.now() - startTime;
          if (this.options.maxTotalExecutionTime !== Infinity && currentTotalTime >= this.options.maxTotalExecutionTime) {
            this.options.onTotalExecutionTimeout?.(this);
            this.abort("total-timeout");
            return void 0;
          }
          if (this.options.maxExecutionTime === Infinity) {
            result = await this.fn(...args);
          } else {
            result = await Promise.race([
              this.fn(...args),
              new Promise((_, reject) => {
                const timeout = setTimeout(() => {
                  this.options.onExecutionTimeout?.(this);
                  this.abort("execution-timeout");
                  reject(
                    new Error(
                      `Execution timeout: ${this.options.maxExecutionTime}ms exceeded`
                    )
                  );
                }, this.options.maxExecutionTime);
                signal.addEventListener(
                  "abort",
                  () => {
                    clearTimeout(timeout);
                    reject(new Error("Aborted"));
                  },
                  { once: true }
                );
              })
            ]);
          }
          if (signal.aborted) {
            return void 0;
          }
          const totalTime = Date.now() - startTime;
          this.#setState({
            executionCount: this.store.state.executionCount + 1,
            isExecuting: false,
            lastExecutionTime: Date.now(),
            totalExecutionTime: totalTime,
            currentAttempt: 0,
            lastResult: result
          });
          this.options.onSuccess?.(result, args, this);
          return result;
        } catch (error) {
          if (error && typeof error === "object" && "name" in error && error.name === "AbortError") {
            return void 0;
          }
          lastError = error instanceof Error ? error : new Error(String(error));
          this.#setState({ lastError });
          this.options.onError?.(lastError, args, this);
          if (attempt < this.#getMaxAttempts()) {
            this.options.onRetry?.(attempt, lastError, this);
            const wait = this.#calculateWait(attempt);
            if (wait > 0) {
              this.#setState({ isExecuting: true, currentAttempt: attempt + 1 });
              await new Promise((resolve) => {
                const timeout = setTimeout(() => {
                  signal.removeEventListener("abort", onAbort);
                  resolve();
                }, wait);
                const onAbort = () => {
                  clearTimeout(timeout);
                  signal.removeEventListener("abort", onAbort);
                  resolve();
                };
                signal.addEventListener("abort", onAbort);
              });
              if (signal.aborted) {
                return void 0;
              }
            }
          }
        } finally {
          this.options.onSettled?.(args, this);
        }
      }
      if (totalTimeoutId) {
        clearTimeout(totalTimeoutId);
      }
      this.#setState({ isExecuting: false });
      this.options.onLastError?.(lastError, this);
      this.options.onSettled?.(args, this);
      if (this.options.throwOnError === "last" && isLastAttempt || this.options.throwOnError === true) {
        throw lastError;
      }
      return void 0;
    };
    this.abort = (reason = "manual") => {
      if (this.#abortController) {
        this.#abortController.abort();
        this.#abortController = null;
        this.#setState({
          isExecuting: false
        });
        this.options.onAbort?.(reason, this);
      }
    };
    this.reset = () => {
      this.#setState(getDefaultAsyncRetryerState());
    };
    this.key = utils.createKey(initialOptions.key);
    this.options = {
      ...defaultOptions,
      ...initialOptions,
      throwOnError: initialOptions.throwOnError ?? (initialOptions.onError ? false : defaultOptions.throwOnError)
    };
    this.#setState(this.options.initialState ?? {});
    eventClient.pacerEventClient.on("d-AsyncRetryer", (event) => {
      if (event.payload.key !== this.key) return;
      this.#setState(event.payload.store.state);
      this.setOptions(event.payload.options);
    });
  }
  #abortController;
  #setState;
  #getEnabled;
  #getMaxAttempts;
  #getBaseWait;
  #calculateJitter;
  #calculateWait;
  /**
   * Returns the current AbortSignal for the executing operation.
   * Use this signal in your async function to make it cancellable.
   * Returns null when not currently executing.
   *
   * @example
   * ```typescript
   * const retryer = new AsyncRetryer(async (userId: string) => {
   *   const signal = retryer.getAbortSignal()
   *   if (signal) {
   *     return fetch(`/api/users/${userId}`, { signal })
   *   }
   *   return fetch(`/api/users/${userId}`)
   * })
   *
   * // Abort will now actually cancel the fetch
   * retryer.abort()
   * ```
   */
  getAbortSignal() {
    return this.#abortController?.signal ?? null;
  }
}
function asyncRetry(fn, initialOptions = {}) {
  const retryer = new AsyncRetryer(fn, initialOptions);
  return retryer.execute;
}
exports.AsyncRetryer = AsyncRetryer;
exports.asyncRetry = asyncRetry;
exports.asyncRetryerOptions = asyncRetryerOptions;
//# sourceMappingURL=async-retryer.cjs.map
